import React, {
  useState, useEffect, memo, useCallback, useRef
} from 'react';
import { connect } from 'react-redux';
import { getFormSyncErrors } from 'redux-form';
import { Trans } from 'react-i18next';

import { hasErrorByFields, showValidationErrorsForStep } from '@common/utils/validation';
import { Button } from '../button';
import Icon from '../icon';
import { Collapsible as CollapsiblePrimitive, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible';
import Group from './group';
import CollapsibleButton from './button';

interface CollapsibleColumnProps {
  children?: React.ReactNode;
  flex?: number;
  grey?: boolean;
  padding?: 'large';
}

const CollapsibleColumnComponent = memo(({ children, flex = 1, grey, padding }: CollapsibleColumnProps) => {
  const classNames = ['Collapsible__Column'];
  if (grey) classNames.push('Collapsible__Column--grey');
  if (padding) classNames.push(`Collapsible__Column--padding-${padding}`);

  return <div className={classNames.join(' ')} style={{ flex }}>{children}</div>;
});
CollapsibleColumnComponent.displayName = 'CollapsibleColumn';

interface CollapsibleProps {
  id?: string;
  title: string;
  children: React.ReactNode;
  className?: string;
  index?: number;
  open?: boolean;
  completed?: boolean;
  optional?: boolean;
  invisible?: boolean;
  onToggle?: () => void;
  onClose?: (e: React.MouseEvent) => void;
  onOpen?: (index?: number) => void;
  onComplete?: (index?: number) => void;
  form?: string;
  errors?: any;
  touch?: any;
  fields?: string[];
}

const CollapsibleComponent = memo(({
  id,
  title,
  children,
  className,
  index = 0,
  open: controlledOpen,
  completed: controlledCompleted,
  // optional,
  invisible,
  onToggle,
  onClose,
  onOpen,
  onComplete,
  errors,
  touch,
  fields,
}: CollapsibleProps) => {
  const [internalOpen, setInternalOpen] = useState(controlledOpen || false);
  const [hasError, setHasError] = useState(false);
  const [isCompleted, setIsCompleted] = useState(controlledCompleted || false);
  const previousOpenRef = useRef<boolean | undefined>(undefined);

  // Use refs for callbacks to avoid infinite loops when parent passes new function references
  const onOpenRef = useRef(onOpen);
  const onCompleteRef = useRef(onComplete);

  // Keep refs updated with latest callbacks
  useEffect(() => {
    onOpenRef.current = onOpen;
    onCompleteRef.current = onComplete;
  });

  // Determine if open state is controlled or internal
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const handleToggle = useCallback(() => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalOpen(!internalOpen);
    }
  }, [onToggle, internalOpen]);

  const handleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClose) {
      onClose(e);
    } else {
      setInternalOpen(false);
    }
  }, [onClose]);

  // Handle completion and error validation when closing
  useEffect(() => {
    const wasOpen = previousOpenRef.current !== isOpen && previousOpenRef.current;

    if (wasOpen && !isOpen) {
      // Closing - check for errors and mark as completed
      let errorFound = false;
      if (errors && fields?.length) {
        errorFound = !!hasErrorByFields(errors, fields);
      }
      setHasError(errorFound);

      // Only auto-complete if not explicitly controlled
      if (controlledCompleted === undefined) {
        setIsCompleted(true);
      }

      if (onCompleteRef.current) onCompleteRef.current(index);
    }

    if (!wasOpen && isOpen) {
      // Opening
      if (onOpenRef.current) onOpenRef.current(index);
      if (errors && touch && fields?.length) {
        showValidationErrorsForStep(errors, fields, touch);
      }
    }

    // Update ref to track state changes
    previousOpenRef.current = isOpen;
  }, [isOpen, errors, fields, touch, index, controlledCompleted]);

  // Handle controlled completed prop
  useEffect(() => {
    if (controlledCompleted !== undefined) {
      setIsCompleted(controlledCompleted);
      if (controlledCompleted && onCompleteRef.current) {
        onCompleteRef.current(index);
      }
    }
  }, [controlledCompleted, index]);

  if (invisible) return null;

  const classNames = ['Collapsible'];
  if (isOpen) classNames.push('Collapsible--open');

  return (
    <div id={id} className={classNames.join(' ')}>
      <CollapsiblePrimitive
        open={isOpen}
        onOpenChange={controlledOpen === undefined ? (open) => setInternalOpen(open) : undefined}
      >
        <CollapsibleTrigger asChild>
          <div className="Collapsible__Header" onClick={handleToggle}>
            {!isOpen && isCompleted && !hasError && (
              <div className="Collapsible__Header__Completed">
                <Icon type="check" />
              </div>
            )}
            {!isOpen && isCompleted && hasError && (
              <div className="Collapsible__Header__Completed--invalid">
                <Icon type="warning" />
              </div>
            )}
            {(!isCompleted || isOpen) && <div className="Collapsible__Header__Index">{index + 1}</div>}
            <h3>{title}</h3>
            {isOpen && (isCompleted || controlledCompleted === undefined) && (
              <Button size="small" type="white" onClick={handleClose}>
                <Trans i18nKey="common:collapsible_done" />
              </Button>
            )}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="Collapsible__Content">
          <div className={`Collapsible__Content__Inner${className ? ` ${className}` : ''}`}>
            {children}
          </div>
        </CollapsibleContent>
      </CollapsiblePrimitive>
    </div>
  );
});
CollapsibleComponent.displayName = 'Collapsible';

// Attach sub-components
const CollapsibleWithSubComponents = CollapsibleComponent as typeof CollapsibleComponent & {
  Group: typeof Group;
  Button: typeof CollapsibleButton;
  Column: typeof CollapsibleColumnComponent;
};

CollapsibleWithSubComponents.Group = Group;
CollapsibleWithSubComponents.Button = CollapsibleButton;
CollapsibleWithSubComponents.Column = CollapsibleColumnComponent;

const mapStateToProps = (state: any, { form = '' }: { form?: string }) => {
  return {
    errors: form ? getFormSyncErrors(form)(state) : null,
  };
};

export default connect(mapStateToProps)(CollapsibleWithSubComponents);

